import { useState, useEffect, useCallback, useRef } from "react";
import type { MouseEvent } from "react";
import { api } from "@/lib/api-client";
import { toast } from "@/components/Toaster";
import { usePermissions } from "@/hooks/usePermissions";

interface EscalatedDocument {
  id: string;
  filename: string;
  classification: string | null;
  currentStep: string;
  priority: number;
  classificationSource: string | null;
  escalationContext: {
    reason?: string;
    triggeredBy?: string;
    ruleName?: string;
    fieldId?: string;
    operatorInput?: string;
    ocrValue?: string;
    category?: "misidentified_type";
  } | null;
  assignedTo: string | null;
  updatedAt: string;
}

interface EscalationQueueProps {
  visible: boolean;
  onSelectDocument: (doc: EscalatedDocument) => void;
  /**
   * WU-1.5 — live-refresh seam. The dashboard bumps this on every
   * ESCALATION_NEW WS event (useEscalationAlerts) so the list + count badge
   * update without waiting for a remount/poll.
   */
  reloadToken?: number;
}

export function EscalationQueue({ visible, onSelectDocument, reloadToken }: EscalationQueueProps) {
  const [documents, setDocuments] = useState<EscalatedDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [reclassifyingId, setReclassifyingId] = useState<string | null>(null);
  const hasLoadedRef = useRef(false);

  // Mirror VerificationStation: warm the permission cache then read it sync.
  const { hasPermission, checkPermission } = usePermissions();
  useEffect(() => {
    void checkPermission("reclassify-doc");
  }, [checkPermission]);
  const canReclassify = hasPermission("reclassify-doc");

  const load = useCallback(async () => {
    if (!visible) return;
    // WU-1.5 — spinner on the initial load only. Live reloadToken refetches
    // keep the list mounted (stale-while-revalidate, same convention as the
    // dashboard's showInitialSkeleton) so a live escalation doesn't flicker
    // the panel through the spinner state.
    if (!hasLoadedRef.current) setIsLoading(true);
    try {
      // Use the queue step type endpoint for escalation step
      const data = await api.get<{
        documents: EscalatedDocument[];
        total: number;
      }>("/queues/escalation?limit=50");
      setDocuments(data.documents);
    } catch {
      // Escalation queue may not have documents — that's fine
      setDocuments([]);
    } finally {
      hasLoadedRef.current = true;
      setIsLoading(false);
    }
  }, [visible]);

  const reclassify = useCallback(
    async (e: MouseEvent, docId: string) => {
      e.stopPropagation();
      // In-flight guard: a double-click would double-count the OCR misfire
      // signal on the fingerprint branch (over-demoting the fingerprint).
      if (reclassifyingId) return;
      setReclassifyingId(docId);
      try {
        await api.post(`/documents/${docId}/reclassify-misfire`, {});
        // reload only on success — a failed post must not phantom-reload
        await load();
      } catch {
        // api-client handles 401; surface other failures (destructive action)
        toast.error("Reclassify failed");
      } finally {
        setReclassifyingId(null);
      }
    },
    [load, reclassifyingId],
  );

  // WU-1.5 — reloadToken in the deps makes a live ESCALATION_NEW event
  // re-fetch the list (badge = documents.length, so the count bumps too).
  useEffect(() => {
    load();
  }, [load, reloadToken]);

  if (!visible) return null;

  function timeAgo(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  }

  return (
    <div className="esc-section">
      <div className="esc-header">
        <span className="esc-title">Escalations</span>
        {documents.length > 0 && (
          <span className="esc-badge">{documents.length}</span>
        )}
      </div>

      {isLoading && (
        <div className="queue-loading">
          <div className="loading-spinner" />
        </div>
      )}

      {!isLoading && documents.length === 0 && (
        <div className="esc-empty">No escalated documents</div>
      )}

      {!isLoading && documents.length > 0 && (
        <div className="esc-list">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="esc-item"
              onClick={() => onSelectDocument(doc)}
            >
              <div className="esc-item-info">
                <div className="esc-item-name">{doc.filename}</div>
                <div className="esc-item-meta">
                  {doc.escalationContext?.ruleName ?? "Escalated"} &middot;{" "}
                  {timeAgo(doc.updatedAt)}
                </div>
                {doc.escalationContext?.category === "misidentified_type" && (
                  <div className="esc-item-misident">
                    <span className="esc-misident-badge">Wrong type</span>
                    {canReclassify && (
                      <button
                        type="button"
                        className="esc-misident-reclassify"
                        onClick={(e) => reclassify(e, doc.id)}
                        disabled={reclassifyingId === doc.id}
                      >
                        {reclassifyingId === doc.id ? "…" : "Reclassify"}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <span
                className={`esc-item-priority ${
                  doc.priority >= 8
                    ? "esc-item-priority--high"
                    : "esc-item-priority--normal"
                }`}
              >
                {doc.priority >= 8 ? "HIGH" : "NORMAL"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export type { EscalatedDocument };
